import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Bouton, Carte, Message } from '@journal/ui';
import { messageErreur, useSession } from '../lib/session.tsx';
import type { UtilisateurCourant } from '../lib/api.ts';

/**
 * Porte de confirmation d'adresse.
 *
 * ── Pourquoi un écran entier, et non une bannière ───────────────────────────
 * Depuis que les routes métier exigent une adresse confirmée, un compte qui ne
 * l'a pas ne reçoit que des 403. Sans cette porte, l'utilisateur verrait
 * l'application se charger puis échouer partout à la fois : menus vides,
 * messages d'erreur en cascade, aucune indication de ce qu'il doit faire. Une
 * bannière au-dessus de ce spectacle ne se lirait pas.
 *
 * Un seul écran, une seule chose à faire.
 *
 * ── L'attente est un état à part ────────────────────────────────────────────
 * Tant que `/auth/me` n'a pas répondu, on ne sait pas. Afficher la porte par
 * défaut la ferait clignoter devant chaque utilisateur déjà confirmé, à chaque
 * chargement ; afficher l'application par défaut ferait clignoter les erreurs.
 * On n'affiche donc rien.
 */
export function PorteConfirmation({ children }: { children: ReactNode }) {
  const { api } = useSession();
  const [moi, setMoi] = useState<UtilisateurCourant | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = useCallback(async () => {
    try {
      setMoi(await api.requete<UtilisateurCourant>('/auth/me'));
    } catch (e) {
      /*
       * Un `/auth/me` en échec ne DOIT PAS fermer la porte.
       *
       * Une coupure réseau enfermerait alors l'utilisateur sur un écran de
       * confirmation qu'il a peut-être déjà faite, sans autre issue que de se
       * déconnecter. En cas de doute, on laisse passer : les routes métier
       * refusent de toute façon, avec leur propre message.
       */
      setErreur(messageErreur(e));
    }
  }, [api]);

  useEffect(() => { void recharger(); }, [recharger]);

  if (!moi) return erreur === null ? null : <>{children}</>;
  if (moi.emailVerified) return <>{children}</>;

  return <EcranConfirmation email={moi.email} surVerification={recharger} />;
}

function EcranConfirmation({
  email, surVerification,
}: {
  email: string;
  surVerification: () => Promise<void>;
}) {
  const { api, deconnecter } = useSession();
  const [envoi, setEnvoi] = useState<'repos' | 'encours' | 'parti'>('repos');
  const [erreur, setErreur] = useState<string | null>(null);

  async function renvoyer() {
    setEnvoi('encours');
    setErreur(null);
    try {
      await api.requete('/auth/resend-verification', { methode: 'POST' });
      setEnvoi('parti');
    } catch (e) {
      setErreur(messageErreur(e));
      setEnvoi('repos');
    }
  }

  return (
    <div className="mx-auto max-w-xl p-6">
      <Carte
        titre="Confirmez votre adresse"
        description={`Un lien a été envoyé à ${email}.`}
      >
        <div className="space-y-4 text-sm">
          {/*
            * La consigne EN PREMIER, et telle que le serveur la formule.
            *
            * C'est le message que renvoie chaque route métier refusée
            * (`EMAIL_NOT_VERIFIED`). L'écran disait la même chose autrement —
            * « cliquez sur ce lien pour activer votre espace » — et deux
            * formulations pour une même règle se lisent comme deux règles.
            */}
          <p>
            Confirmez votre adresse avant d’utiliser l’application : un lien
            vous a été envoyé lors de la création du compte.
          </p>
          <p>
            Cliquez sur ce lien pour activer votre espace. Il expire au bout de
            vingt-quatre heures.
          </p>

          {envoi === 'parti' ? (
            <Message ton="succes">
              Un nouveau lien vient de partir. Pensez à regarder dans les
              indésirables.
            </Message>
          ) : null}
          {erreur ? <Message ton="erreur">{erreur}</Message> : null}

          <div className="flex flex-wrap gap-2">
            <Bouton onClick={() => void renvoyer()} disabled={envoi === 'encours'}>
              {envoi === 'encours' ? 'Envoi…' : 'Renvoyer le lien'}
            </Bouton>
            {/* Pour qui vient de cliquer sur le lien dans un autre onglet :
                sans cela, il faudrait se déconnecter et se reconnecter. */}
            <Bouton variante="discret" onClick={() => void surVerification()}>
              J’ai confirmé
            </Bouton>
            <Bouton variante="discret" onClick={() => void deconnecter()}>
              Se déconnecter
            </Bouton>
          </div>
        </div>
      </Carte>
    </div>
  );
}
